import _ from 'lodash';
import config from 'application-config';
import { IS_DEVEL } from 'utils/environment';
import { prependUrlProtocol } from 'utils/helpers';

function withDynamicHostname(urlParam: string) {
	try {
		const url = new URL(prependUrlProtocol(urlParam, 'https'));
		const pathname = url.pathname === '/' ? '' : url.pathname;
		return `${url.protocol}//${window.location.hostname}${pathname}`;
	} catch (e) {
		return urlParam;
	}
}

const domains = {
	FRONTEND_ADMIN: 'frontend.admin',
	FRONTEND_CLIENT: 'frontend.client',
	BACKEND_ADMIN: 'backend.admin',
	BACKEND_CLIENT: 'backend.client',
};
type ConfigDomain = {
	hostname: string;
	port?: number;
};

type Config = {
	GA_SC_GENERAL: string;
	api: {
		adminBackend: string;
		clientBackend: string;
	};
	domains: {
		backend: {
			admin: ConfigDomain;
			client: ConfigDomain;
		};
		frontend: {
			admin: ConfigDomain;
			client: ConfigDomain;
		};
	};
	cname: string;
};
/**
 * UrlHelper class to help get resource url for our application
 * Singleton
 */
class UrlHelper {
	config: Config;

	/**
	 * Create UrlHelper instance
	 *
	 * @param configParam {Object} url configuration
	 * @return UrlHelper {Object} instance
	 */
	constructor(configParam: Config) {
		this.config = configParam;
	}

	get cname() {
		return this.config.cname;
	}

	/**
	 * Get backend host url
	 * @return {string}  admin host url
	 */
	get clientBackend() {
		return IS_DEVEL ? this.config.api.clientBackend : withDynamicHostname(this.config.api.clientBackend);
	}

	/**
	 * Get admin host url
	 * @return {string}  admin host url
	 */
	get adminBackend() {
		return this.config.api.adminBackend;
	}

	/**
	 * Get client host url
	 * @return {string}  client host url
	 */
	get client() {
		return this.getDomain(domains.FRONTEND_CLIENT);
	}

	/**
	 * Get admin host url
	 * @return {string}  admin host url
	 */
	get admin() {
		return this.getDomain(domains.FRONTEND_ADMIN);
	}

	/**
	 * @param {string} domain
	 * @example getDomain('FRONTEND_ADMIN')
	 * @return {object} domain map
	 */
	getDomain(domain) {
		const domainProps = _.get(config.domains, domain);
		return domainProps.port ? `${domainProps.hostname}:${domainProps.port}` : domainProps.hostname;
	}

	/**
	 * Get url of published story
	 */
	getPublishedUrl(clientStoryId: string, cardId?: string) {
		const params = cardId ? `?cardId=${cardId}` : '';

		if (IS_DEVEL) {
			return `https://story.dev.story-cards.com/${clientStoryId}/${params}`;
		}
		return prependUrlProtocol(`${this.getDomain(domains.FRONTEND_CLIENT)}/${clientStoryId}/${params}`, 'https');
	}
}

const UrlHelperInstance = new UrlHelper(config);

export const location = {
	client: {
		/**
		 * Is client app embedded in admin dashboard
		 * @desc is [cardEditor | cardPreview | embedPreview]
		 */
		isPreview:
			document.referrer &&
			document.referrer !== window.location.origin &&
			document.referrer.includes(UrlHelperInstance.admin),

		isPreviewRoute: window.location.pathname.match(/preview.html/g) !== null,

		isEmbedDevPreview: window.location.href.match(/preview.html\?embedDev/g) !== null,

		/**
		 * @desc is opened by screenshot API
		 */
		isScreenshot: () => window.location.href.match(/[&|?]screenshot/) !== null,
	},
};

export default UrlHelperInstance;
